import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, Divider, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Stack, Switch,
  CircularProgress, Alert, Autocomplete, IconButton,
  FormControlLabel, Chip, RadioGroup, Radio, Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  NotificationsActive as AlertIcon,
  PhoneAndroid as PushIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  DirectionsCar as CarIcon,
  CreditCard as DvlaIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Five-category model:
 *   safety   → check_type IN ('SAFETY','LOCATION')
 *   delivery → check_type IN ('DELIVERY','OUTGOING_DELIVERY')
 *   loan     → check_type IN ('LOAN','LOAN_RETURN')
 *   mot      → MOT/Tax triggers
 *   dvla     → DVLA triggers
 */
type AlertCategory = 'safety' | 'delivery' | 'loan' | 'mot' | 'dvla';


type TriggerEvent =
  | 'check_fail' | 'check_pass' | 'check_any'
  | 'delivery_check_completed' | 'loan_check_completed'
  | 'mot_due_soon' | 'mot_expired' | 'tax_due_soon' | 'tax_expired'
  | 'dvla_licence_expiring' | 'dvla_licence_expired'
  | 'dvla_card_expiring' | 'dvla_card_expired'
  | 'dvla_points_change' | 'dvla_new_endorsement' | 'dvla_licence_status_change';

type ScopeType = 'all' | 'location' | 'asset_type' | 'asset_list' | 'template' | 'asset_template' | 'dvla_staff_list' | 'asset_tag';

interface Recipient {
  recipient_type: 'user' | 'team' | 'job_tag' | 'email';
  user_id?: string;
  team_id?: string;
  job_tag_id?: string;
  email?: string;
  _label?: string;
}

interface AlertRule {
  rule_id?: string;
  name: string;
  trigger_event: TriggerEvent;
  trigger_events?: TriggerEvent[];
  rule_check_types?: string[] | null;
  scope_type: ScopeType;
  scope_location_id?: string;
  scope_asset_type_ids?: string[];
  scope_asset_ids?: string[];
  scope_staff_ids?: string[];
  scope_job_tag_ids?: string[];
  scope_tag_template_ids?: string[];
  scope_template_id?: string;
  scope_asset_template_asset_id?: string;
  send_email: boolean;
  send_push: boolean;
  send_pdf_attachment?: boolean;
  enabled: boolean;
  recipients?: Recipient[];
}

interface Props {
  open: boolean;
  rule: AlertRule | null;
  onClose: () => void;
  onSaved: () => void;
  /** When false the MOT / Tax alert category is hidden from the picker */
  businessMotTaxEnabled?: boolean;
  /** When false the DVLA alert category is hidden from the picker */
  businessDvlaEnabled?: boolean;
  /** When false the "By location" scope option is hidden (Sites / Locations module is off) */
  businessLocationsEnabled?: boolean;
  /** "Site" or "Location" — used to label location scope options correctly */
  locationLabel?: string;
}

// ─── Reference data interfaces ────────────────────────────────────────────────

interface Location { locationId: string; locationName: string; }
interface AssetType { asset_type_id: string; type_name: string; }
interface Asset { asset_id: string; asset_name: string; }
interface Template { template_id: string; template_name: string; }
interface User { user_id: string; first_name: string; last_name: string; email: string; }
interface Team { group_id: string; group_name: string; }
interface JobTag { job_tag_id: string; tag_name: string; tag_scope?: 'both' | 'user' | 'asset'; }
interface DvlaStaff { id: string; first_name: string; last_name: string; }

/** GET /users returns camelCase; normalize for stable labels and recipient IDs */
function normalizeUser(raw: Record<string, unknown>): User {
  return {
    user_id: String(raw.user_id ?? raw.userId ?? ''),
    first_name: String(raw.first_name ?? raw.firstName ?? ''),
    last_name: String(raw.last_name ?? raw.lastName ?? ''),
    email: String(raw.email ?? ''),
  };
}

/** GET /teams returns camelCase groupId / groupName */
function normalizeTeam(raw: Record<string, unknown>): Team {
  return {
    group_id: String(raw.group_id ?? raw.groupId ?? ''),
    group_name: String(raw.group_name ?? raw.groupName ?? ''),
  };
}

function unwrapList(res: { data?: unknown }): unknown[] {
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object' && Array.isArray((d as { data?: unknown }).data)) {
    return (d as { data: unknown[] }).data;
  }
  return [];
}

// ─── Trigger options ─────────────────────────────────────────────────────────

const TRIGGER_OPTIONS: { value: TriggerEvent; label: string; desc: string; color: string; category: AlertCategory }[] = [
  { value: 'check_fail', label: 'Check fails',     desc: 'Alert when a check is submitted with a FAIL status.',      color: '#DC2626', category: 'safety' },
  { value: 'check_pass', label: 'Check passes',    desc: 'Alert when a check is submitted with a PASS status.',      color: '#059669', category: 'safety' },
  { value: 'check_any',  label: 'Any check event', desc: 'Alert on any completed check regardless of pass or fail.', color: '#0288D1', category: 'safety' },
  { value: 'mot_due_soon',  label: 'MOT due soon',        desc: 'Alert when a vehicle MOT is expiring within 30 days.', color: '#D97706', category: 'mot' },
  { value: 'mot_expired',   label: 'MOT expired',         desc: 'Alert when a vehicle MOT has expired.',               color: '#DC2626', category: 'mot' },
  { value: 'tax_due_soon',  label: 'Road tax due soon',   desc: 'Alert when vehicle road tax is expiring within 30 days.', color: '#D97706', category: 'mot' },
  { value: 'tax_expired',   label: 'Road tax expired',    desc: 'Alert when vehicle road tax has expired.',              color: '#DC2626', category: 'mot' },
  { value: 'dvla_licence_expiring',      label: 'Licence expiring',          desc: 'Alert when a driver licence is due to expire (within 6 months).', color: '#7C3AED', category: 'dvla' },
  { value: 'dvla_licence_expired',       label: 'Licence expired',           desc: 'Alert when a driver licence entitlement has expired.',           color: '#DC2626', category: 'dvla' },
  { value: 'dvla_card_expiring',         label: 'Photocard expiring',        desc: 'Alert when the physical photocard is due to expire.',            color: '#7C3AED', category: 'dvla' },
  { value: 'dvla_card_expired',          label: 'Photocard expired',         desc: 'Alert when the physical photocard has expired.',                 color: '#DC2626', category: 'dvla' },
  { value: 'dvla_points_change',         label: 'Penalty points increased',  desc: 'Alert when a driver’s penalty points increase.',                 color: '#B45309', category: 'dvla' },
  { value: 'dvla_new_endorsement',       label: 'New endorsement added',     desc: 'Alert when a new endorsement is added to a driver licence.',     color: '#B45309', category: 'dvla' },
  { value: 'dvla_licence_status_change', label: 'Licence status changed',    desc: 'Alert when a licence status changes (e.g. to Suspended).',      color: '#DC2626', category: 'dvla' },
];

const ALERT_CATEGORIES: { value: AlertCategory; label: string; subtitle: string; color: string; bg: string; Icon: React.ElementType }[] = [
  { value: 'safety',   label: 'Safety Checks',  subtitle: 'Standard safety & location checks', color: '#1D4ED8', bg: '#EFF6FF', Icon: CheckIcon },
  { value: 'delivery', label: 'Delivery Checks', subtitle: 'Outgoing delivery checks',          color: '#065F46', bg: '#ECFDF5', Icon: CheckIcon },
  { value: 'loan',     label: 'Loan Checks',     subtitle: 'Equipment loan checks',             color: '#6D28D9', bg: '#F5F3FF', Icon: CheckIcon },
  { value: 'mot',      label: 'MOT / Tax',       subtitle: 'MOT or road tax expiry',            color: '#B45309', bg: '#FFFBEB', Icon: CarIcon   },
  { value: 'dvla',     label: 'DVLA',            subtitle: 'Driver licence events',             color: '#9D174D', bg: '#FDF2F8', Icon: DvlaIcon  },
];

/**
 * Infer the 5-category from an existing rule's trigger_event + rule_check_types.
 */
function categoryOf(t: TriggerEvent, ruleCheckTypes?: string[] | null): AlertCategory {
  if (['mot_due_soon', 'mot_expired', 'tax_due_soon', 'tax_expired'].includes(t)) return 'mot';
  if (t.startsWith('dvla_')) return 'dvla';
  if (t === 'delivery_check_completed') return 'delivery';
  if (t === 'loan_check_completed') return 'loan';
  if (ruleCheckTypes?.length) {
    const upper = ruleCheckTypes.map((x: string) => x.toUpperCase());
    if (upper.some((x: string) => ['DELIVERY', 'OUTGOING_DELIVERY'].includes(x))) return 'delivery';
    if (upper.some((x: string) => ['LOAN', 'LOAN_RETURN'].includes(x))) return 'loan';
  }
  return 'safety';
}

// ─── Component ────────────────────────────────────────────────────────────────

const AlertRuleDrawer: React.FC<Props> = ({
  open,
  rule,
  onClose,
  onSaved,
  businessMotTaxEnabled = true,
  businessDvlaEnabled = true,
  businessLocationsEnabled = true,
  locationLabel = 'Location',
}) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isNew = !rule?.rule_id;

  // ── Form state ──────────────────────────────────────────────────────────────
  const [alertCategory, setAlertCategory] = useState<AlertCategory>('safety');
  // PDF report attachment only applies to check-completion categories (a generated check PDF exists);
  // MOT/Tax and DVLA alerts have no underlying check to attach a report from.
  const isCheckCategory = alertCategory === 'safety' || alertCategory === 'delivery' || alertCategory === 'loan';
  const [name, setName] = useState('');
  const [triggerEvent, setTriggerEvent] = useState<TriggerEvent>('check_fail');
  const [selectedMotTriggers, setSelectedMotTriggers] = useState<TriggerEvent[]>([]);
  const [selectedDvlaTriggers, setSelectedDvlaTriggers] = useState<TriggerEvent[]>([]);
  const [scopeType, setScopeType] = useState<ScopeType>('all');
  const [scopeLocationId, setScopeLocationId] = useState('');
  const [scopeAssetTypeIds, setScopeAssetTypeIds] = useState<string[]>([]);
  const [scopeAssetIds, setScopeAssetIds] = useState<string[]>([]);
  const [scopeStaffIds, setScopeStaffIds] = useState<string[]>([]);
  const [scopeJobTagIds, setScopeJobTagIds] = useState<string[]>([]);
  const [scopeTagTemplateIds, setScopeTagTemplateIds] = useState<string[]>([]);
  const [scopeTemplateId, setScopeTemplateId] = useState('');
  const [scopeAssetTemplateAssetId, setScopeAssetTemplateAssetId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendPush, setSendPush] = useState(false);
  const [sendPdfAttachment, setSendPdfAttachment] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  // ── Reference data ──────────────────────────────────────────────────────────
  const [locations, setLocations] = useState<Location[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [dvlaStaff, setDvlaStaff] = useState<DvlaStaff[]>([]);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [recipientType, setRecipientType] = useState<'user' | 'team' | 'job_tag' | 'email'>('user');
  const [freeEmail, setFreeEmail] = useState('');

  // ── Load reference data on open ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    Promise.all([
      axios.get(`${API_BASE_URL}/locations`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/asset-types`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/assets`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/check-templates`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/users`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/teams`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/job-tags`, { headers: authHeaders }),
      axios.get(`${API_BASE_URL}/dvla-staff/staff`, { headers: authHeaders }).catch(() => ({ data: [] })),
    ]).then(([locRes, atRes, asRes, tRes, uRes, teamRes, jtRes, dvlaRes]) => {
      setLocations(locRes.data?.data || locRes.data || []);
      setAssetTypes(atRes.data?.data || atRes.data || []);
      setAssets(asRes.data?.data || asRes.data || []);
      setTemplates(tRes.data?.data || tRes.data || []);
      setUsers(unwrapList(uRes).map((u) => normalizeUser(u as Record<string, unknown>)));
      setTeams(unwrapList(teamRes).map((g) => normalizeTeam(g as Record<string, unknown>)));
      setJobTags(jtRes.data?.data || jtRes.data || []);
      const rawStaff: unknown[] = Array.isArray(dvlaRes.data) ? dvlaRes.data : (dvlaRes.data?.data || dvlaRes.data?.staff || []);
      setDvlaStaff(rawStaff.map((s: any) => ({ id: s.id, first_name: s.first_name || '', last_name: s.last_name || '' })));
    }).catch(() => {});
  }, [open]);

  // ── Populate from existing rule ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (rule) {
      let cat = categoryOf(rule.trigger_event ?? 'check_fail', rule.rule_check_types);
      // If the feature for this category is now disabled, fall back to 'safety'
      if (cat === 'mot' && !businessMotTaxEnabled) cat = 'safety';
      if (cat === 'dvla' && !businessDvlaEnabled) cat = 'safety';
      setAlertCategory(cat);
      setName(rule.name ?? '');
      setTriggerEvent(rule.trigger_event ?? 'check_fail');

      if (cat === 'mot') {
        const triggers = (rule.trigger_events ?? [rule.trigger_event as TriggerEvent]).filter(t => categoryOf(t) === 'mot');
        setSelectedMotTriggers(triggers.length ? triggers : [rule.trigger_event as TriggerEvent]);
        if (triggers.length) setTriggerEvent(triggers[0]);
        setSelectedDvlaTriggers([]);
      } else if (cat === 'dvla') {
        const triggers = (rule.trigger_events ?? [rule.trigger_event as TriggerEvent]).filter(t => categoryOf(t) === 'dvla');
        setSelectedDvlaTriggers(triggers.length ? triggers : [rule.trigger_event as TriggerEvent]);
        if (triggers.length) setTriggerEvent(triggers[0]);
        setSelectedMotTriggers([]);
      } else {
        // safety/delivery/loan — use single check trigger event
        setTriggerEvent(['check_fail', 'check_pass', 'check_any'].includes(rule.trigger_event) ? rule.trigger_event : 'check_fail');
        setSelectedMotTriggers([]);
        setSelectedDvlaTriggers([]);
      }

      setScopeType((rule.scope_type === 'location' && !businessLocationsEnabled) ? 'all' : (rule.scope_type ?? 'all'));
      setScopeLocationId(rule.scope_location_id ?? '');
      setScopeAssetTypeIds(rule.scope_asset_type_ids ?? []);
      setScopeAssetIds(rule.scope_asset_ids ?? []);
      setScopeStaffIds(rule.scope_staff_ids ?? []);
      setScopeJobTagIds(rule.scope_job_tag_ids ?? []);
      setScopeTagTemplateIds(rule.scope_tag_template_ids ?? []);
      setScopeTemplateId(rule.scope_template_id ?? '');
      setScopeAssetTemplateAssetId(rule.scope_asset_ids?.[0] ?? '');
      setSendEmail(rule.send_email !== false);
      setSendPush(!!rule.send_push);
      setSendPdfAttachment(!!rule.send_pdf_attachment);
      setEnabled(rule.enabled !== false);
      setRecipients((rule.recipients ?? []).map((r: any) => ({
        ...r,
        _label: r.user_id
          ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim()
          : r.team_id ? r.team_name
          : r.job_tag_id ? r.job_tag_name
          : r.email,
      })));
    } else {
      setAlertCategory('safety');
      setName('');
      setTriggerEvent('check_fail');
      setSelectedMotTriggers([]);
      setSelectedDvlaTriggers([]);
      setScopeType('all');
      setScopeLocationId('');
      setScopeAssetTypeIds([]);
      setScopeAssetIds([]);
      setScopeStaffIds([]);
      setScopeJobTagIds([]);
      setScopeTagTemplateIds([]);
      setScopeTemplateId('');
      setScopeAssetTemplateAssetId('');
      setSendEmail(true);
      setSendPush(false);
      setSendPdfAttachment(false);
      setEnabled(true);
      setRecipients([]);
    }
    setError('');
    setStatusMsg('');
    setFreeEmail('');
  }, [open, rule, businessMotTaxEnabled, businessDvlaEnabled, businessLocationsEnabled]);

  // ── Category switch ──────────────────────────────────────────────────────────
  const handleCategoryChange = (cat: AlertCategory) => {
    setAlertCategory(cat);
    setSelectedMotTriggers([]);
    setSelectedDvlaTriggers([]);
    if (cat === 'safety' || cat === 'delivery' || cat === 'loan') {
      setTriggerEvent('check_fail');
      if (scopeType === 'dvla_staff_list') setScopeType('all');
    } else if (cat === 'mot') {
      setTriggerEvent('mot_due_soon');
      if (['template', 'asset_template', 'dvla_staff_list'].includes(scopeType)) setScopeType('all');
    } else {
      setTriggerEvent('dvla_licence_expiring');
      setScopeType('all');
    }
  };

  const toggleMotTrigger = (t: TriggerEvent) => {
    setSelectedMotTriggers(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
    setTriggerEvent(t);
  };

  const toggleDvlaTrigger = (t: TriggerEvent) => {
    setSelectedDvlaTriggers(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
    setTriggerEvent(t);
  };

  // ── Recipient helpers ────────────────────────────────────────────────────────
  const addUserRecipient = (user: User | null) => {
    if (!user) return;
    if (recipients.find(r => r.user_id === user.user_id)) return;
    setRecipients(prev => [...prev, {
      recipient_type: 'user', user_id: user.user_id,
      _label: `${user.first_name} ${user.last_name}`,
    }]);
  };
  const addTeamRecipient = (team: Team | null) => {
    if (!team) return;
    if (recipients.find(r => r.team_id === team.group_id)) return;
    setRecipients(prev => [...prev, { recipient_type: 'team', team_id: team.group_id, _label: team.group_name }]);
  };
  const addJobTagRecipient = (jt: JobTag | null) => {
    if (!jt) return;
    if (recipients.find(r => r.job_tag_id === jt.job_tag_id)) return;
    setRecipients(prev => [...prev, { recipient_type: 'job_tag', job_tag_id: jt.job_tag_id, _label: jt.tag_name }]);
  };
  const addEmailRecipient = () => {
    const e = freeEmail.trim();
    if (!e || !/\S+@\S+\.\S+/.test(e)) return;
    if (recipients.find(r => r.email === e)) return;
    setRecipients(prev => [...prev, { recipient_type: 'email', email: e, _label: e }]);
    setFreeEmail('');
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');
    if (!name.trim()) { setError('Please enter a name for this alert.'); return; }
    if (!recipients.length) { setError('Add at least one recipient.'); return; }
    if (alertCategory === 'mot' && selectedMotTriggers.length === 0) {
      setError('Select at least one MOT / road tax event.'); return;
    }
    if (alertCategory === 'dvla' && selectedDvlaTriggers.length === 0) {
      setError('Select at least one DVLA event.'); return;
    }
    if (scopeType === 'asset_tag' && scopeJobTagIds.length === 0) {
      setError('Select at least one asset tag.'); return;
    }

    setSaving(true);

    let effectiveTriggerEvent: TriggerEvent;
    let effectiveTriggerEvents: TriggerEvent[] | undefined;
    let effectiveRuleCheckTypes: string[] | null = null;

    if (alertCategory === 'mot') {
      effectiveTriggerEvent = selectedMotTriggers[0] ?? 'mot_due_soon';
      effectiveTriggerEvents = selectedMotTriggers.length > 1 ? selectedMotTriggers : undefined;
    } else if (alertCategory === 'dvla') {
      effectiveTriggerEvent = selectedDvlaTriggers[0] ?? 'dvla_licence_expiring';
      effectiveTriggerEvents = selectedDvlaTriggers.length > 1 ? selectedDvlaTriggers : undefined;
    } else {
      effectiveTriggerEvent = triggerEvent;
      effectiveTriggerEvents = undefined;
      // Phase 1: set rule_check_types to scope the rule to this check category
      if (alertCategory === 'delivery') effectiveRuleCheckTypes = ['DELIVERY', 'OUTGOING_DELIVERY'];
      else if (alertCategory === 'loan') effectiveRuleCheckTypes = ['LOAN', 'LOAN_RETURN'];
      // 'safety' → null (matches all non-delivery/loan checks, backward compat)
    }

    const payload: Record<string, unknown> = {
      name,
      trigger_event: effectiveTriggerEvent,
      trigger_events: effectiveTriggerEvents,
      rule_check_types: effectiveRuleCheckTypes,
      scope_type: scopeType,
      scope_location_id: scopeLocationId || null,
      scope_asset_type_ids: scopeType === 'asset_type' ? scopeAssetTypeIds : [],
      scope_asset_ids: scopeType === 'asset_list'
        ? scopeAssetIds
        : scopeType === 'asset_template' && scopeAssetTemplateAssetId
          ? [scopeAssetTemplateAssetId]
          : [],
      scope_staff_ids: scopeType === 'dvla_staff_list' ? scopeStaffIds : [],
      scope_job_tag_ids: scopeType === 'asset_tag' ? scopeJobTagIds : [],
      scope_tag_template_ids: scopeType === 'asset_tag' ? scopeTagTemplateIds : [],
      scope_template_id: (scopeType === 'template' || scopeType === 'asset_template') ? scopeTemplateId || null : null,
      send_email: sendEmail,
      send_push: sendPush,
      send_pdf_attachment: isCheckCategory ? sendPdfAttachment : false,
      enabled,
      recipients: recipients.map(r => ({
        recipient_type: r.recipient_type,
        user_id: r.user_id,
        team_id: r.team_id,
        job_tag_id: r.job_tag_id,
        email: r.email,
      })),
    };

    try {
      if (isNew) {
        await axios.post(`${API_BASE_URL}/communication-alerts`, payload, { headers });
      } else {
        await axios.put(`${API_BASE_URL}/communication-alerts/${rule!.rule_id}`, payload, { headers });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save alert rule');
    } finally {
      setSaving(false);
    }
  };

  // ── Test ─────────────────────────────────────────────────────────────────────
  const handleTest = async () => {
    if (!rule?.rule_id) return;
    setTesting(true); setStatusMsg('');
    try {
      const res = await axios.post(
        `${API_BASE_URL}/communication-alerts/${rule.rule_id}/test`,
        { test_email: testEmail || undefined },
        { headers }
      );
      setStatusMsg(res.data.message || 'Test sent');
    } catch (err: any) {
      setStatusMsg('Failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  const currentCat = ALERT_CATEGORIES.find(c => c.value === alertCategory) ?? ALERT_CATEGORIES[0]!;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '90vh' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AlertIcon color="error" />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          {isNew ? 'New Alert Rule' : `Edit: ${name}`}
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} size="small" />}
          label={<Typography variant="caption">{enabled ? 'Enabled' : 'Disabled'}</Typography>}
          sx={{ mr: 0 }}
        />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {/* ── 1. Name ── */}
          <TextField
            label="Alert name *"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            placeholder='e.g. "Forklift fail — notify manager"'
          />

          {/* ── 2. Alert type category picker — 5 categories ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
              Alert type
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 0.5 }}>
              {ALERT_CATEGORIES
                .filter(cat => {
                  if (cat.value === 'mot' && !businessMotTaxEnabled) return false;
                  if (cat.value === 'dvla' && !businessDvlaEnabled) return false;
                  return true;
                })
                .map(cat => {
                  const selected = alertCategory === cat.value;
                  return (
                    <Box
                      key={cat.value}
                      onClick={() => handleCategoryChange(cat.value)}
                      sx={{
                        p: 1.5, border: '2px solid',
                        borderColor: selected ? cat.color : 'divider',
                        borderRadius: 2, cursor: 'pointer',
                        bgcolor: selected ? cat.bg : 'background.paper',
                        transition: 'all .15s', textAlign: 'center',
                      }}
                    >
                      <cat.Icon sx={{ color: selected ? cat.color : 'text.disabled', fontSize: 24, mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ color: selected ? cat.color : 'text.primary', lineHeight: 1.2, fontSize: '0.8rem' }}>
                        {cat.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                        {cat.subtitle}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          </Box>

          {/* ── 3. Trigger events ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
              When should this alert fire?
            </Typography>

            {/* Safety/Delivery/Loan — check triggers (single select) */}
            {(alertCategory === 'safety' || alertCategory === 'delivery' || alertCategory === 'loan') && (
              <Stack spacing={1}>
                {['check_fail', 'check_pass', 'check_any'].map(ev => {
                  const opt = TRIGGER_OPTIONS.find(o => o.value === ev)!;
                  if (!opt) return null;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => setTriggerEvent(opt.value as TriggerEvent)}
                      sx={{
                        p: 1.5, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                        borderColor: triggerEvent === opt.value ? opt.color : 'divider',
                        bgcolor: triggerEvent === opt.value ? `${opt.color}10` : 'background.paper',
                        transition: 'all .15s',
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} sx={{ color: triggerEvent === opt.value ? opt.color : 'text.primary' }}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {alertCategory === 'safety' ? opt.desc
                          : alertCategory === 'delivery' ? opt.desc.replace('a check', 'a Delivery Check')
                          : opt.desc.replace('a check', 'a Loan Check')}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {/* MOT triggers — multi-select */}
            {alertCategory === 'mot' && (
              <Stack spacing={1}>
                {TRIGGER_OPTIONS.filter(o => o.category === 'mot').map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => toggleMotTrigger(opt.value)}
                    sx={{
                      p: 1.5, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                      borderColor: selectedMotTriggers.includes(opt.value) ? opt.color : 'divider',
                      bgcolor: selectedMotTriggers.includes(opt.value) ? `${opt.color}10` : 'background.paper',
                      transition: 'all .15s',
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                    }}
                  >
                    <Checkbox
                      checked={selectedMotTriggers.includes(opt.value)}
                      size="small"
                      sx={{ p: 0, mt: 0.1, color: opt.color, '&.Mui-checked': { color: opt.color } }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleMotTrigger(opt.value)}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: selectedMotTriggers.includes(opt.value) ? opt.color : 'text.primary' }}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}

            {/* DVLA triggers — multi-select */}
            {alertCategory === 'dvla' && (
              <Stack spacing={1}>
                {TRIGGER_OPTIONS.filter(o => o.category === 'dvla').map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => toggleDvlaTrigger(opt.value)}
                    sx={{
                      p: 1.5, border: '2px solid', borderRadius: 2, cursor: 'pointer',
                      borderColor: selectedDvlaTriggers.includes(opt.value) ? opt.color : 'divider',
                      bgcolor: selectedDvlaTriggers.includes(opt.value) ? `${opt.color}10` : 'background.paper',
                      transition: 'all .15s',
                      display: 'flex', alignItems: 'flex-start', gap: 1,
                    }}
                  >
                    <Checkbox
                      checked={selectedDvlaTriggers.includes(opt.value)}
                      size="small"
                      sx={{ p: 0, mt: 0.1, color: opt.color, '&.Mui-checked': { color: opt.color } }}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleDvlaTrigger(opt.value)}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: selectedDvlaTriggers.includes(opt.value) ? opt.color : 'text.primary' }}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* ── 4. Scope ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
              {alertCategory === 'dvla' ? 'Which drivers?' : alertCategory === 'mot' ? 'Which vehicles?' : `Which assets? (${currentCat.label})`}
            </Typography>
            {alertCategory === 'mot' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                MOT / Road tax alerts apply only to assets that have a number plate registered.
              </Typography>
            )}
            {alertCategory === 'dvla' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                DVLA alerts apply to drivers enrolled in your DVLA licence management.
              </Typography>
            )}

            {/* DVLA scope options */}
            {alertCategory === 'dvla' && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup value={scopeType === 'dvla_staff_list' ? 'dvla_staff_list' : 'all'} onChange={e => setScopeType(e.target.value as ScopeType)}>
                  <FormControlLabel value="all"             control={<Radio size="small" />} label="All drivers in the business" />
                  <FormControlLabel value="dvla_staff_list" control={<Radio size="small" />} label="Specific drivers" />
                </RadioGroup>
              </FormControl>
            )}

            {/* Check / MOT / Safety / Delivery / Loan scope options */}
            {alertCategory !== 'dvla' && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup value={scopeType} onChange={e => setScopeType(e.target.value as ScopeType)}>
                  <FormControlLabel value="all"        control={<Radio size="small" />} label="All assets in the business" />
                  {businessLocationsEnabled && (
                    <FormControlLabel value="location" control={<Radio size="small" />} label={`Assets at a specific ${locationLabel}`} />
                  )}
                  <FormControlLabel value="asset_type" control={<Radio size="small" />} label="Specific asset type(s)" />
                  <FormControlLabel value="asset_list" control={<Radio size="small" />} label="Specific individual assets" />
                  <FormControlLabel value="asset_tag"  control={<Radio size="small" />} label="Assets with a specific tag" />
                  {(alertCategory === 'safety' || alertCategory === 'delivery' || alertCategory === 'loan') && (
                    <FormControlLabel value="template"       control={<Radio size="small" />} label="Checks on a specific template (all assets)" />
                  )}
                  {(alertCategory === 'safety' || alertCategory === 'delivery' || alertCategory === 'loan') && (
                    <FormControlLabel value="asset_template" control={<Radio size="small" />} label="Specific asset + specific template" />
                  )}
                </RadioGroup>
              </FormControl>
            )}

            {/* Scope sub-selectors — DVLA staff */}
            {scopeType === 'dvla_staff_list' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                options={dvlaStaff}
                getOptionLabel={s => `${s.first_name} ${s.last_name}`}
                value={dvlaStaff.filter(s => scopeStaffIds.includes(s.id))}
                onChange={(_, v) => setScopeStaffIds(v.map(x => x.id))}
                renderInput={p => <TextField {...p} label="Select drivers *" />}
                renderTags={(v, getTagProps) => v.map((opt, i) => (
                  <Chip label={`${opt.first_name} ${opt.last_name}`} size="small" {...getTagProps({ index: i })} />
                ))}
              />
            )}

            {/* Scope sub-selectors — location */}
            {scopeType === 'location' && businessLocationsEnabled && (
              <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <InputLabel>{locationLabel} *</InputLabel>
                <Select value={scopeLocationId} label={`${locationLabel} *`} onChange={e => setScopeLocationId(e.target.value)}>
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {locations.map(l => <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {scopeType === 'asset_type' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                options={assetTypes}
                getOptionLabel={o => o.type_name}
                value={assetTypes.filter(t => scopeAssetTypeIds.includes(t.asset_type_id))}
                onChange={(_, v) => setScopeAssetTypeIds(v.map(x => x.asset_type_id))}
                renderInput={p => <TextField {...p} label="Asset types *" />}
                renderTags={(v, getTagProps) => v.map((opt, i) => (
                  <Chip label={opt.type_name} size="small" {...getTagProps({ index: i })} />
                ))}
              />
            )}

            {scopeType === 'asset_list' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                options={assets}
                getOptionLabel={o => o.asset_name}
                value={assets.filter(a => scopeAssetIds.includes(a.asset_id))}
                onChange={(_, v) => setScopeAssetIds(v.map(x => x.asset_id))}
                renderInput={p => <TextField {...p} label="Assets *" />}
                renderTags={(v, getTagProps) => v.map((opt, i) => (
                  <Chip label={opt.asset_name} size="small" {...getTagProps({ index: i })} />
                ))}
              />
            )}

            {scopeType === 'asset_tag' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={jobTags.filter(t => t.tag_scope !== 'user')}
                  getOptionLabel={o => o.tag_name}
                  value={jobTags.filter(t => scopeJobTagIds.includes(t.job_tag_id))}
                  onChange={(_, v) => setScopeJobTagIds(v.map(x => x.job_tag_id))}
                  renderInput={p => <TextField {...p} label="Asset tags *" />}
                  renderTags={(v, getTagProps) => v.map((opt, i) => (
                    <Chip label={opt.tag_name} size="small" {...getTagProps({ index: i })} />
                  ))}
                />
                {templates.length > 0 && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={templates}
                    getOptionLabel={o => o.template_name}
                    value={templates.filter(t => scopeTagTemplateIds.includes(t.template_id))}
                    onChange={(_, v) => setScopeTagTemplateIds(v.map(x => x.template_id))}
                    renderInput={p => (
                      <TextField
                        {...p}
                        label="Filter by template (optional)"
                        placeholder="Any template…"
                        helperText="Only fire for checks using one of these templates"
                      />
                    )}
                    renderTags={(v, getTagProps) => v.map((opt, i) => (
                      <Chip label={opt.template_name} size="small" {...getTagProps({ index: i })} />
                    ))}
                  />
                )}
              </Stack>
            )}

            {scopeType === 'template' && (
              <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <InputLabel>Check template *</InputLabel>
                <Select value={scopeTemplateId} label="Check template *" onChange={e => setScopeTemplateId(e.target.value)}>
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {templates.map(t => <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {scopeType === 'asset_template' && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset *</InputLabel>
                  <Select value={scopeAssetTemplateAssetId} label="Asset *" onChange={e => setScopeAssetTemplateAssetId(e.target.value)}>
                    <MenuItem value=""><em>Select asset…</em></MenuItem>
                    {assets.map(a => <MenuItem key={a.asset_id} value={a.asset_id}>{a.asset_name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Check template *</InputLabel>
                  <Select value={scopeTemplateId} label="Check template *" onChange={e => setScopeTemplateId(e.target.value)}>
                    <MenuItem value=""><em>Select template…</em></MenuItem>
                    {templates.map(t => <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Box>

          {/* ── 5. Recipients ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
              Who gets alerted?
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              {(['user', 'team', 'job_tag', 'email'] as const).map(t => (
                <Chip
                  key={t}
                  label={{ user: 'User', team: 'Team', job_tag: 'Tag', email: 'Email' }[t]}
                  size="small"
                  variant={recipientType === t ? 'filled' : 'outlined'}
                  color={recipientType === t ? 'primary' : 'default'}
                  onClick={() => setRecipientType(t)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>

            {recipientType === 'user' && (
              <Autocomplete
                size="small"
                options={users}
                getOptionLabel={u => `${u.first_name} ${u.last_name} (${u.email})`}
                onChange={(_, v) => addUserRecipient(v)}
                renderInput={p => <TextField {...p} label="Add user" />}
              />
            )}
            {recipientType === 'team' && (
              <Autocomplete
                size="small"
                options={teams}
                getOptionLabel={t => t.group_name}
                onChange={(_, v) => addTeamRecipient(v)}
                renderInput={p => <TextField {...p} label="Add team" />}
              />
            )}
            {recipientType === 'job_tag' && (
              <Autocomplete
                size="small"
                options={jobTags}
                getOptionLabel={j => j.tag_name}
                onChange={(_, v) => addJobTagRecipient(v)}
                renderInput={p => <TextField {...p} label="Add tag" />}
              />
            )}
            {recipientType === 'email' && (
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Email address"
                  type="email"
                  value={freeEmail}
                  onChange={e => setFreeEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addEmailRecipient()}
                />
                <Button variant="outlined" onClick={addEmailRecipient} size="small">Add</Button>
              </Stack>
            )}

            {recipients.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {recipients.map((r, i) => (
                  <Chip
                    key={i}
                    label={r._label || r.email}
                    size="small"
                    onDelete={() => setRecipients(prev => prev.filter((_, j) => j !== i))}
                    color={r.recipient_type === 'user' ? 'primary' : r.recipient_type === 'team' ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            {recipients.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                No recipients added yet.
              </Typography>
            )}
          </Box>

          {/* ── 6. Channels ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5, fontSize: '0.7rem' }}>
              Alert channels
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <EmailIcon color={sendEmail ? 'primary' : 'disabled'} fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>Email</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send a detailed HTML alert email with full event details
                  </Typography>
                </Box>
                <Switch checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
              </Box>
              {isCheckCategory && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, pl: 4.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', opacity: sendEmail ? 1 : 0.5 }}>
                  <PdfIcon color={sendEmail && sendPdfAttachment ? 'primary' : 'disabled'} fontSize="small" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>Attach PDF report</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Include the generated check report PDF as an email attachment
                    </Typography>
                  </Box>
                  <Switch
                    checked={sendPdfAttachment}
                    disabled={!sendEmail}
                    onChange={e => setSendPdfAttachment(e.target.checked)}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                <PushIcon color={sendPush ? 'primary' : 'disabled'} fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>Mobile push notification</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send a push to user/team recipients who have the mobile app installed
                  </Typography>
                </Box>
                <Switch checked={sendPush} onChange={e => setSendPush(e.target.checked)} />
              </Box>
            </Box>
          </Box>

          {/* ── Preview summary ── */}
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${currentCat.color}08`, border: `1px solid ${currentCat.color}30` }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              This alert will fire when:
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: currentCat.color }}>
              {alertCategory === 'mot'
                ? selectedMotTriggers.length ? selectedMotTriggers.map(t => TRIGGER_OPTIONS.find(o => o.value === t)?.label).join(' or ') : 'No event selected'
                : alertCategory === 'dvla'
                ? selectedDvlaTriggers.length ? selectedDvlaTriggers.map(t => TRIGGER_OPTIONS.find(o => o.value === t)?.label).join(' or ') : 'No event selected'
                : `${TRIGGER_OPTIONS.find(o => o.value === triggerEvent)?.label ?? triggerEvent} — ${currentCat.label}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {' '}—{' '}
              {alertCategory === 'dvla'
                ? scopeType === 'dvla_staff_list' ? `${scopeStaffIds.length} specific driver(s)` : 'all drivers'
                : scopeType === 'all' ? 'any asset in the business'
                : scopeType === 'location' ? 'assets in the selected location'
                : scopeType === 'asset_type' ? `${scopeAssetTypeIds.length} asset type(s)`
                : scopeType === 'asset_list' ? `${scopeAssetIds.length} specific asset(s)`
                : scopeType === 'asset_tag' ? `assets tagged ${scopeJobTagIds.length} selected tag(s)`
                : scopeType === 'template' ? 'checks from the selected template'
                : 'the selected asset + template'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              → {recipients.length} recipient(s){sendEmail ? ' via email' : ''}{sendPush ? (sendEmail ? ' + push' : ' via push') : ''}{sendEmail && isCheckCategory && sendPdfAttachment ? ' + PDF report' : ''}
            </Typography>
          </Box>

          {/* ── Test (edit mode only) ── */}
          {!isNew && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Send a test alert</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    placeholder="Email (leave blank for your account)"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    type="email"
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" startIcon={<SendIcon />} onClick={handleTest} disabled={testing}>
                    {testing ? 'Sending…' : 'Send test'}
                  </Button>
                </Stack>
                {statusMsg && (
                  <Alert severity={statusMsg.startsWith('Failed') ? 'error' : 'success'} sx={{ mt: 1 }} onClose={() => setStatusMsg('')}>
                    {statusMsg}
                  </Alert>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving…' : isNew ? 'Create Alert' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AlertRuleDrawer;
